import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Ruler, Paperclip, Square, BookOpen, Check, X } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';

interface MeasurementObject {
  id: string;
  name: string;
  length: number;
  unit: string;
  color: string;
  shape: 'rectangle' | 'circle' | 'triangle';
}

interface MeasurementUnit {
  id: string;
  name: string;
  icon: string;
  color: string;
  size: number;
}

interface MeasurementConfig {
  objects: MeasurementObject[];
  units: MeasurementUnit[];
  showAnswer: boolean;
  allowInteraction: boolean;
  theme: 'primary' | 'secondary' | 'success' | 'warning';
  question?: string;
}

interface MeasurementComponentProps {
  resource: {
    id?: string;
    type: string;
    name?: string;
    description?: string;
    config?: MeasurementConfig;
    objects?: MeasurementObject[];
    units?: MeasurementUnit[];
    showAnswer?: boolean;
    allowInteraction?: boolean;
    theme?: 'primary' | 'secondary' | 'success' | 'warning';
    question?: string;
    [key: string]: any;
  };
  onAction?: (action: string, data?: any) => void;
}

const getConfigValue = (resource: any, key: string, defaultValue: any) => {
  return resource.config?.[key] ?? resource[key] ?? defaultValue;
};

const getIconComponent = (iconName: string) => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    ruler: Ruler,
    paperclip: Paperclip,
    cube: Square,
    book: BookOpen,
  };
  return iconMap[iconName] || Ruler;
};

const getThemeColors = (theme: string) => {
  const themes = {
    primary: 'bg-blue-500 text-white',
    secondary: 'bg-gray-500 text-white',
    success: 'bg-green-500 text-white',
    warning: 'bg-yellow-500 text-white',
  };
  return themes[theme as keyof typeof themes] || themes.primary;
};

const getShapeStyle = (shape: string, color: string) => {
  const baseStyle = `w-full h-full ${color}`;
  
  switch (shape) {
    case 'circle':
      return `${baseStyle} rounded-full`;
    case 'triangle':
      return `${baseStyle} clip-path-triangle`;
    case 'rectangle':
    default:
      return `${baseStyle} rounded`;
  }
};

export const MeasurementComponent: React.FC<MeasurementComponentProps> = ({ resource }) => {
  const { t } = useTranslation();
  const [selectedObject, setSelectedObject] = useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);

  const objects = getConfigValue(resource, 'objects', [
    { id: '1', name: t('measurement.pencil'), length: 5, unit: 'paperclips', color: 'bg-yellow-400', shape: 'rectangle' },
    { id: '2', name: t('measurement.eraser'), length: 3, unit: 'cubes', color: 'bg-pink-400', shape: 'rectangle' },
    { id: '3', name: t('measurement.pen'), length: 6, unit: 'paperclips', color: 'bg-blue-400', shape: 'rectangle' },
  ]);

  const units = getConfigValue(resource, 'units', [
    { id: 'paperclips', name: t('measurement.paperclips'), icon: 'paperclip', color: 'bg-gray-400', size: 1 },
    { id: 'cubes', name: t('measurement.cubes'), icon: 'cube', color: 'bg-red-400', size: 1 },
    { id: 'books', name: t('measurement.books'), icon: 'book', color: 'bg-green-400', size: 1 },
  ]);

  const showAnswer = getConfigValue(resource, 'showAnswer', false);
  const allowInteraction = getConfigValue(resource, 'allowInteraction', true);
  const theme = getConfigValue(resource, 'theme', 'primary');
  const question = getConfigValue(resource, 'question', t('measurement.defaultQuestion'));

  const handleObjectSelect = useCallback((objectId: string) => {
    setSelectedObject(objectId);
    setSelectedUnit(null);
    setShowResults(false);
  }, []);

  const handleUnitSelect = useCallback((unitId: string) => {
    setSelectedUnit(unitId);
  }, []);

  const handleMeasure = useCallback(() => {
    if (!selectedObject || !selectedUnit) return;

    const object = objects.find((obj: any) => obj.id === selectedObject);
    const unit = units.find((u: any) => u.id === selectedUnit);
    
    if (!object || !unit) return;

    // Calculate measurement based on unit size
    const measurement = Math.round(object.length / unit.size);
    
    setMeasurements(prev => ({
      ...prev,
      [`${selectedObject}-${selectedUnit}`]: measurement
    }));
    
    setShowResults(true);
  }, [selectedObject, selectedUnit, objects, units]);

  const handleReset = useCallback(() => {
    setSelectedObject(null);
    setSelectedUnit(null);
    setMeasurements({});
    setShowResults(false);
  }, []);

  const getMeasurementResult = (objectId: string, unitId: string) => {
    return measurements[`${objectId}-${unitId}`];
  };

  const isCorrectMeasurement = (objectId: string, unitId: string) => {
    const object = objects.find((obj: any) => obj.id === objectId);
    const unit = units.find((u: any) => u.id === unitId);
    
    if (!object || !unit) return false;
    
    const expected = Math.round(object.length / unit.size);
    const actual = getMeasurementResult(objectId, unitId);
    
    return actual === expected;
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{resource.name || t('measurement.objectMeasurement')}</h3>
        <p className="text-gray-600 mb-4">{resource.description || t('measurement.measureObjectsWithNonStandardUnits')}</p>
        {question && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 font-medium">{question}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
        {/* Objects Section */}
        <div>
          <h4 className="text-base md:text-lg font-medium text-gray-700 mb-3 md:mb-4">{t('measurement.objectsToMeasure')}</h4>
          <div className="space-y-2 md:space-y-3">
            {objects.map((object: any) => (
              <motion.div
                key={object.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`
                  p-3 md:p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 overflow-hidden
                  ${selectedObject === object.id 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                onClick={() => allowInteraction && handleObjectSelect(object.id)}
              >
                <div className="flex flex-col items-center text-center space-y-2 md:space-y-3">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-8 md:w-16 md:h-12 ${object.color} rounded flex items-center justify-center`}>
                      <div className={getShapeStyle(object.shape, 'w-8 h-6 md:w-12 md:h-9')}></div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <h5 className="text-sm md:text-base font-medium text-gray-800 break-words">{object.name}</h5>
                    <p className="text-xs md:text-sm text-gray-600 break-words">
                      {t('measurement.length')}: {object.length} {object.unit}
                    </p>
                  </div>
                  {selectedObject === object.id && (
                    <div className="flex-shrink-0">
                      <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full ${getThemeColors(theme)} flex items-center justify-center`}>
                        <Check className="w-3 h-3 md:w-4 md:h-4" />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Units Section */}
        <div>
          <h4 className="text-base md:text-lg font-medium text-gray-700 mb-3 md:mb-4">{t('measurement.measurementUnits')}</h4>
          <div className="space-y-2 md:space-y-3">
            {units.map((unit: any) => (
              <motion.div
                key={unit.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`
                  p-3 md:p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 overflow-hidden
                  ${selectedUnit === unit.id 
                    ? 'border-green-400 bg-green-50' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                onClick={() => allowInteraction && handleUnitSelect(unit.id)}
              >
                <div className="flex flex-col items-center text-center space-y-2 md:space-y-3">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 md:w-12 md:h-12 ${unit.color} rounded flex items-center justify-center`}>
                      {React.createElement(getIconComponent(unit.icon), { className: 'w-5 h-5 md:w-6 md:h-6 text-white' })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <h5 className="text-sm md:text-base font-medium text-gray-800 break-words">{unit.name}</h5>
                    <p className="text-xs md:text-sm text-gray-600 break-words">
                      {t('measurement.size')}: {unit.size} {t('measurement.unit')}
                    </p>
                  </div>
                  {selectedUnit === unit.id && (
                    <div className="flex-shrink-0">
                      <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-green-500 text-white flex items-center justify-center">
                        <Check className="w-3 h-3 md:w-4 md:h-4" />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Measurement Actions */}
      {allowInteraction && (
        <div className="mt-4 md:mt-6 p-3 md:p-4 bg-gray-50 rounded-lg">
            <div className="flex-1 min-w-0">
            {selectedObject && selectedUnit ? (
                <p className="text-xs md:text-sm text-gray-600 break-words">
                {t('measurement.readyToMeasure')}: <span className="font-medium">{objects.find((o: any) => o.id === selectedObject)?.name}</span> 
                {t('measurement.with')} <span className="font-medium">{units.find((u: any) => u.id === selectedUnit)?.name}</span>
                </p>
            ) : (
                <p className="text-xs md:text-sm text-gray-600 break-words">
                {t('measurement.selectObjectAndUnit')}
                </p>
            )}
            </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex space-x-2">
              <button
                onClick={handleMeasure}
                disabled={!selectedObject || !selectedUnit}
                className={`px-3 py-2 md:px-4 md:py-2 rounded-lg text-white text-xs md:text-sm font-medium transition-colors ${
                  selectedObject && selectedUnit 
                    ? getThemeColors(theme) 
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {t('measurement.measure')}
              </button>
              <button
                onClick={handleReset}
                className="px-3 py-2 md:px-4 md:py-2 bg-gray-500 text-white rounded-lg text-xs md:text-sm font-medium hover:bg-gray-600 transition-colors"
              >
                {t('measurement.reset')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {showResults && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-4 md:mt-6 p-4 md:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg md:text-xl font-semibold text-blue-800 flex items-center">
              <Ruler className="w-5 h-5 md:w-6 md:h-6 mr-2" />
              {t('measurement.measurementResults')}
            </h4>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0"></div>
                <span className="text-xs text-gray-600 break-words">{t('measurement.correct')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                <span className="text-xs text-gray-600 break-words">{t('measurement.incorrect')}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {objects.map((object: any) => (
              <motion.div
                key={object.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm overflow-hidden"
              >
                            <div className="flex items-center space-x-3 mb-3">
              <div className="flex-shrink-0">
                <div className={`w-8 h-6 ${object.color} rounded flex items-center justify-center`}>
                  <div className={getShapeStyle(object.shape, 'w-6 h-4')}></div>
                </div>
              </div>
              <div className="min-w-0 flex-1">
                    <h5 className="font-semibold text-gray-800 break-words">{object.name}</h5>
                    <p className="text-sm text-gray-600 break-words">
                      {t('measurement.actualLength')}: {object.length} {object.unit}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {units.map((unit: any) => {
                    const result = getMeasurementResult(object.id, unit.id);
                    const isCorrect = isCorrectMeasurement(object.id, unit.id);
                    const expected = Math.round(object.length / unit.size);
                    
                    return (
                      <motion.div
                        key={unit.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className={`
                          relative p-3 rounded-lg border-2 transition-all duration-200 overflow-hidden
                          ${result ? (isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50') : 'border-gray-200 bg-gray-50'}
                        `}
                      >
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="flex-shrink-0">
                            <div className={`w-6 h-6 ${unit.color} rounded flex items-center justify-center`}>
                              {React.createElement(getIconComponent(unit.icon), { className: 'w-3 h-3 text-white' })}
                            </div>
                          </div>
                          <span className="font-medium text-sm text-gray-700 break-words min-w-0 flex-1">{unit.name}</span>
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 break-words">{t('measurement.measurement')}:</span>
                            <span className={`font-bold text-lg ml-2 ${result ? (isCorrect ? 'text-green-600' : 'text-red-600') : 'text-gray-400'}`}>
                              {result || '?'}
                            </span>
                          </div>

                          {showAnswer && result && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600 break-words">{t('measurement.expected')}:</span>
                              <span className="text-sm font-medium text-gray-700 ml-2">{expected}</span>
                            </div>
                          )}

                          {result && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600 break-words">{t('measurement.status')}:</span>
                              <div className="flex items-center space-x-1 ml-2">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <span className={`text-xs font-medium ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                  {isCorrect ? t('measurement.correct') : t('measurement.incorrect')}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {result && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${
                              isCorrect ? 'bg-green-500' : 'bg-red-500'
                            }`}
                          >
                            {isCorrect ? (
                              <Check className="w-3 h-3 text-white" />
                            ) : (
                              <X className="w-3 h-3 text-white" />
                            )}
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          {showAnswer && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4 p-3 bg-blue-100 border border-blue-300 rounded-lg"
            >
              <p className="text-sm text-blue-800 text-center">
                💡 <strong>{t('measurement.tip')}:</strong> {t('measurement.measurementTip')}
              </p>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Instructions */}
      {!allowInteraction && (
        <div className="mt-4 md:mt-6 p-3 md:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs md:text-sm text-yellow-800 break-words">
            💡 <strong>{t('measurement.instructions')}:</strong> {t('measurement.instructionsDescription')}
          </p>
        </div>
      )}
    </div>
  );
};
